import { Injectable, NgZone } from "@angular/core";
import { FirebaseX } from '@ionic-native/firebase-x/ngx';
import { Platform, NavController } from '@ionic/angular';
import { AppService } from './app.service';
import { LogService } from "./log.service";


@Injectable()
export class NotificationService {
    public leadId: number | null = null;
    public active: boolean = false;
    public followUpAlert: boolean = false;
    public whatsAppAlert: boolean = false;
    public notificationTitle: string = "";
    // push popup keeps its own text: the signalR popup can be on screen at the same time
    public whatsAppMessage: string = "";
    public link: string = "";
    public message: string = "";
    private hasWhatsAppPhone: boolean = false;

    constructor(
        private firebaseX: FirebaseX,
        private platform: Platform,
        private appService: AppService,
        private navCtrl: NavController,
        private ngZone: NgZone,
        private logService: LogService,
    ) { }

    get canWork(): boolean { return this.platform.is("cordova"); }
    get isAndroid(): boolean { return this.platform.is("android"); }
    get isIOS(): boolean { return this.platform.is("ios"); }

    // Check that native Firebase plugin is actually available (especially on iOS,
    // where cordova-plugin-firebasex requires cordova-ios >= 7 and may not be installed)
    private get isFirebasePluginAvailable(): boolean {
        const w: any = window as any;
        // cordova-plugin-firebasex clobbers `FirebasePlugin`
        if (w && w.FirebasePlugin) {
            return true;
        }
        // Fallback: older/alternative refs
        if (w && w.cordova && w.cordova.plugins && (w.cordova.plugins.firebase || w.cordova.plugins.firebaseMessaging)) {
            return true;
        }
        return false;
    }

    public async subscribe(userId: number, hasWhatsAppPhone: boolean): Promise<void> {
        this.hasWhatsAppPhone = hasWhatsAppPhone;
        await this.platform.ready();

        if (this.canWork) {
            try {
                const pluginAvailable = this.isFirebasePluginAvailable;
                this.logService.add(`canWork. userId: ${userId}, pluginAvailable: ${pluginAvailable}`);

                await this.firebaseX.subscribe(`fix-${userId}`).then(function (res) { });
                this.logService.add(`check hasPermission`);
                this.firebaseX.hasPermission().then((hasPerm: boolean) => {
                    this.logService.add(`hasPerm: ${hasPerm}`);
                    if (!hasPerm) {
                        this.firebaseX.grantPermission().then((permissionGranted) => { });
                        this.logService.add(`grantPermission`);
                    }
                });
                if (this.isAndroid) {
                    this.logService.add(`isAndroid`);
                    this.firebaseX.onTokenRefresh().subscribe((token: string) => {
                        this.logService.add(`onTokenRefresh success: ${token}`)
                    });
                    this.logService.add(`onTokenRefresh`);
                    this.firebaseX.getToken().then((token) => {
                        this.logService.add(`getToken success: ${token}`);
                    });
                    this.logService.add(`getToken`);
                } else if (this.isIOS) {
                    this.logService.add(`isIOS`);

                    // No getToken() here: on iOS FCM cannot issue a token before APNS handed one back
                    // and rejects with com.google.fcm 505 (#318). onTokenRefresh() delivers the token
                    // by itself once APNS registration completes.
                    this.firebaseX.onTokenRefresh().subscribe((token: string) => {
                        this.logService.add(`onTokenRefresh success: ${token}`)
                    });
                    this.logService.add(`onTokenRefresh`);

                    this.firebaseX.onApnsTokenReceived().subscribe((token: string) => {
                        this.logService.add(`onApnsTokenReceived success: ${token}`)
                    })
                    this.logService.add(`onApnsTokenReceived`);
                    this.firebaseX.getAPNSToken().then((token) => {
                        this.logService.add(`getAPNSToken success: ${token}`);
                    });
                    this.logService.add(`getAPNSToken`);
                }
                this.logService.add(`before onMessageReceived`);
                this.firebaseX.onMessageReceived().subscribe(msg => {
                    this.logService.add(`onMessageReceived: ${JSON.stringify(msg)}`);
                    if ((msg.messageType === 'notification' || msg.messageType == 'data') && msg.leadClientId > 0) {
                        this.logService.add(`notification leadClientId`);
                        this.leadId = msg.leadClientId;
                        const hasWhatsAppPhone = this.hasWhatsAppPhone;

                        setTimeout(() => {
                            this.appService.leadClientsGroup.forEach((x) => {
                                var lc = x.leadClients.find(c => c.id == msg.leadClientId);
                                if (lc) {
                                    if (!lc.countOfWhatsAppMessages) {
                                        lc.countOfWhatsAppMessages = 0;
                                    }

                                    lc.countOfWhatsAppMessages++;
                                }
                            })
                        }, 100);

                        if (msg.tap) {
                            this.ngZone.run(() => {
                                if (hasWhatsAppPhone) {
                                    this.navCtrl.navigateForward(`/leadClients/${msg.leadClientId}/whatsapp`);
                                } else {
                                    this.navCtrl.navigateForward(`/leadClients/${msg.leadClientId}`);
                                }
                            });
                        } else {
                            this.ngZone.run(() => {
                                if (hasWhatsAppPhone) {
                                    // firebasex never passes a `notification` object: on Android title/body
                                    // sit at the top level of the message, on iOS inside the APNS `aps.alert`.
                                    const alert = (msg.aps && msg.aps.alert) || {};
                                    this.notificationTitle = msg.title || alert.title || "";
                                    this.whatsAppMessage = msg.body || alert.body || "";
                                    this.whatsAppAlert = true;
                                } else {
                                    this.leadId = msg.leadClientId;
                                    this.link = `/leadClients/${msg.leadClientId}`;
                                    this.active = true;
                                }
                            });
                        }
                    }
                    else if ((msg.messageType === "notification" || msg.messageType == 'data') && msg.leadId > 0) {
                        this.logService.add(`notification leadId`);
                        this.leadId = msg.leadId;
                        if (msg.tap) {
                            this.ngZone.run(() => {
                                this.navCtrl.navigateForward(`/leadClients/${msg.leadId}`);
                            });
                        } else {
                            this.ngZone.run(() => {
                                this.active = true;
                            });
                        }
                    }
                    else if ((msg.messageType === 'notification' || msg.messageType == 'data') && msg.followUpLeadId > 0) {
                        this.logService.add(`notification followUpLeadId`);
                        this.leadId = msg.followUpLeadId;
                        if (msg.tap || msg.tap == "background") {
                            this.ngZone.run(() => {
                                this.navCtrl.navigateForward(`/leadClients/${msg.followUpLeadId}`);
                            });
                        } else {
                            this.ngZone.run(() => {
                                this.followUpAlert = true;
                            });
                        }
                    }
                });
            } catch (err) {
                const msg = this.appService.getErrorMessage(err);
                // Ignore missing plugin on platforms where Firebase is not available,
                // so the app does not show a generic error dialog on iOS.
                if (typeof msg === "string" && msg.indexOf("plugin_not_installed") !== -1) {
                    this.logService.add(`Firebase plugin not installed on this platform, skipping subscription. Details: ${msg}`);
                    return;
                }

                this.logService.add(`Firebase subscription error. ${msg}`);
                this.appService.showError("Firebase subscription error", msg);
            }
        } else {
            this.logService.add(`NO canWork (not running under Cordova). userId: ${userId}, platforms: ${this.platform.platforms()}`);
        }
    }

    public unsubscribe(userId: number): void {
        if (!this.canWork) {
            return;
        }

        if (!this.isFirebasePluginAvailable) {
            this.logService.add(`Cannot unsubscribe from Firebase topic on this platform – Firebase plugin not available. userId: ${userId}`);
            return;
        }

        this.firebaseX.unsubscribe(`fix-${userId}`);
    }
}
